/**
 * Staff-only visibility marker for admin-only alarms — a compact badge icon
 * rendered beside an alarm chip whenever the alarm definition is not public
 * (`is_public === false`, the backend `view_admin_alarms` gate). Non-staff
 * users never receive these alarms, so the badge only ever appears to
 * viewers who hold the gate; it tells them customers cannot see the alarm.
 *
 * Contains:
 * - AdminOnlyAlarmBadge — icon + explanatory tooltip, null for public alarms
 */
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {object} props
 * @param {object} [props.def] - Alarm definition from the reference catalog;
 *   only `is_public` is read. Absent/unknown defs render nothing.
 */
function AdminOnlyAlarmBadge({ def }) {
  const { t } = useTranslation();

  if (def?.is_public !== false) return null;

  return (
    <Tooltip title={t('alarms.adminOnlyMessage', 'Only staff and admin users can see this alarm.')}>
      <AdminPanelSettingsOutlinedIcon
        role="img"
        aria-label={t('alarms.adminOnly', 'Admin Only')}
        sx={{ fontSize: 18, color: 'var(--status-info-text)', flexShrink: 0 }}
      />
    </Tooltip>
  );
}

AdminOnlyAlarmBadge.propTypes = {
  def: PropTypes.shape({ is_public: PropTypes.bool }),
};

export default AdminOnlyAlarmBadge;
